import { Helmet } from 'react-helmet-async';
import { useState, useEffect, createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import api from '@/lib/api';

const SITE_URL = 'https://hirojet.com';
// 1200×630 — the shape LinkedIn, X and Slack actually render. The old default was
// the 512×512 app icon, which every one of them letterboxed or cropped.
const DEFAULT_IMAGE = `${SITE_URL}/brand/og-default.png`;

const SeoContext = createContext({});

export function SeoProvider({ children }) {
  const { data: seoData = {} } = useQuery({
    queryKey: ['seo-public-page-settings'],
    queryFn: () => api.get('/seo/public-page-settings').then(r => r.data || {}),
    staleTime: 1_800_000,   // marketing copy — 30 min is plenty
    gcTime: 3_600_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  return <SeoContext.Provider value={seoData}>{children}</SeoContext.Provider>;
}

const PAGE_KEY_MAP = {
  '/': 'home',
  '/services': 'services',
  '/about': 'about',
  '/blog': 'blog',
  '/contact': 'contact',
  '/careers': 'careers',
  '/salary-calculator': 'salary',
  '/jobs': 'jobs',
};

// Renders JSON-LD into the <head> via portal + cleanup
function JsonLd({ data }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    };
  }, [JSON.stringify(data)]);

  return null;
}

export default function SEO({
  title, description, keywords, image, path, url,
  type = 'website', article, noindex = false,
  breadcrumbs, schema
}) {
  const dbOverrides = useContext(SeoContext);

  const pageKey = PAGE_KEY_MAP[path] || PAGE_KEY_MAP[url] || '';
  const overrides = dbOverrides[pageKey] || {};

  const pageTitle = (title || overrides.title)
    ? (title || overrides.title) + ' | HiroJet'
    : 'HiroJet - AI-Powered Tech Recruitment';
  const metaDescription = description || overrides.description || 'AI-powered tech recruiting delivering top-tier engineers globally in 48 hours.';
  const metaImage = image || overrides.og_image || DEFAULT_IMAGE;
  const metaUrl = (url || path) ? SITE_URL + (url || path) : SITE_URL;
  const metaKeywords = keywords || overrides.keywords || '';
  const shouldNoindex = noindex || overrides.noindex || false;

  // Build structured data
  const schemas = [];

  // Default Organization
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HiroJet',
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description: metaDescription,
    sameAs: ['https://www.linkedin.com/company/hirojet'],
    contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', email: 'hello@hirojet.com' }
  });

  // Article
  if (type === 'article' && article) {
    schemas.push({
      '@context': 'https://schema.org', '@type': 'Article',
      headline: title || '', description: metaDescription, image: metaImage,
      author: { '@type': 'Person', name: article.author || 'HiroJet' },
      publisher: { '@type': 'Organization', name: 'HiroJet', logo: { '@type': 'ImageObject', url: DEFAULT_IMAGE } },
      datePublished: article.publishedDate || '',
      mainEntityOfPage: { '@type': 'WebPage', '@id': metaUrl },
    });
  }

  // Breadcrumbs
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
        item: SITE_URL + crumb.path,
      }))
    });
  }

  // Custom schema from page
  if (schema) {
    if (Array.isArray(schema)) {
      schemas.push(...schema);
    } else {
      schemas.push(schema);
    }
  }

  return (
    <>
      <Helmet
        title={pageTitle}
        htmlAttributes={{ lang: 'en' }}
        meta={[
          { name: 'description', content: metaDescription },
          ...(metaKeywords ? [{ name: 'keywords', content: metaKeywords }] : []),
          ...(shouldNoindex ? [{ name: 'robots', content: 'noindex,nofollow' }] : [{ name: 'robots', content: 'index,follow,max-image-preview:large,max-snippet:-1' }]),
          { property: 'og:type', content: type },
          { property: 'og:title', content: pageTitle },
          { property: 'og:description', content: metaDescription },
          { property: 'og:image', content: metaImage },
          { property: 'og:url', content: metaUrl },
          { property: 'og:site_name', content: 'HiroJet' },
          { property: 'og:locale', content: 'en_US' },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: pageTitle },
          { name: 'twitter:description', content: metaDescription },
          { name: 'twitter:image', content: metaImage },
          { name: 'twitter:site', content: '@hirojet' },
          ...(article ? [
            { property: 'article:published_time', content: article.publishedDate || '' },
            { property: 'article:author', content: article.author || 'HiroJet' }
          ] : []),
          { name: 'geo.region', content: 'US' },
        ]}
        link={[
          { rel: 'canonical', href: metaUrl },
          { rel: 'alternate', hreflang: 'en', href: metaUrl },
          { rel: 'alternate', hreflang: 'x-default', href: metaUrl },
        ]}
      />
      <JsonLd data={schemas} />
    </>
  );
}

export { SEO };
